import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { ToolPageShell, inputCls, Field } from "@/components/AdminTools";
import { Plus, Trash2, Save, X, CheckCircle2, Circle, ListTodo, User } from "lucide-react";

const fmt = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export default function AdminTodos() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", detail: "" });
  const [showDone, setShowDone] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin-todos");
      setItems(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setEditing("new");
    setForm({ title: "", detail: "" });
  };
  const startEdit = (it) => {
    setEditing(it.id);
    setForm({ title: it.title || "", detail: it.detail || "" });
  };
  const cancel = () => {
    setEditing(null);
    setForm({ title: "", detail: "" });
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    try {
      if (editing === "new") {
        await api.post("/admin-todos", form);
        toast.success("Task added.");
      } else {
        await api.patch(`/admin-todos/${editing}`, form);
        toast.success("Task updated.");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleDone = async (it) => {
    try {
      await api.patch(`/admin-todos/${it.id}`, { done: !it.done });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await api.delete(`/admin-todos/${id}`);
      toast.success("Deleted.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const open = items.filter((t) => !t.done);
  const done = items.filter((t) => t.done);
  const visible = showDone ? items : open;

  return (
    <ToolPageShell
      eyebrow="Admin · Operations"
      title="Admin To Do"
      intro="A shared task list across all admins. Synced server-side — anyone signed in as admin sees the same queue."
      actions={
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="todos-toggle-done"
            onClick={() => setShowDone((v) => !v)}
            className="text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-full px-3 py-1.5 hover:bg-slate-50"
          >
            {showDone ? "Hide done" : "Show all"}
          </button>
          <button
            type="button"
            data-testid="todos-new-btn"
            onClick={startNew}
            className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800"
          >
            <Plus className="w-3.5 h-3.5" /> New task
          </button>
        </div>
      }
    >
      <div className="grid grid-cols-3 gap-3 mb-6">
        <Stat label="Open" value={open.length} testId="todos-stat-open" />
        <Stat label="Done" value={done.length} testId="todos-stat-done" />
        <Stat label="Total" value={items.length} testId="todos-stat-total" />
      </div>

      {editing && (
        <div data-testid="todos-form" className="rounded-2xl border border-slate-200 bg-white p-6 mb-6 space-y-4">
          <Field label="Title" required>
            <input
              data-testid="todos-form-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className={inputCls}
            />
          </Field>
          <Field label="Detail" hint="Markdown / plain text. Visible to all admins.">
            <textarea
              rows={4}
              data-testid="todos-form-detail"
              value={form.detail}
              onChange={(e) => setForm({ ...form, detail: e.target.value })}
              className={`${inputCls} resize-y`}
            />
          </Field>
          <div className="flex items-center gap-2 pt-1">
            <button
              data-testid="todos-form-save"
              onClick={save}
              className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800"
            >
              <Save className="w-3.5 h-3.5" /> Save
            </button>
            <button
              onClick={cancel}
              className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2 text-sm font-medium hover:bg-slate-50"
            >
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
          </div>
        </div>
      )}

      {loading && <div className="text-sm text-slate-500">Loading…</div>}

      <ul className="space-y-2" data-testid="todos-list">
        {visible.map((t) => (
          <li
            key={t.id}
            data-testid={`todo-${t.id}`}
            className={`rounded-2xl border bg-white p-4 flex items-start gap-3 transition-colors ${
              t.done ? "border-slate-100 opacity-70" : "border-slate-200"
            }`}
          >
            <button
              type="button"
              onClick={() => toggleDone(t)}
              data-testid={`todo-toggle-${t.id}`}
              aria-label={t.done ? "Mark as not done" : "Mark as done"}
              className={`mt-0.5 w-5 h-5 rounded-full border flex items-center justify-center shrink-0 transition-colors ${
                t.done
                  ? "bg-emerald-600 border-emerald-600 text-white"
                  : "border-slate-300 hover:border-slate-500 text-transparent"
              }`}
            >
              {t.done ? <CheckCircle2 className="w-3 h-3" /> : <Circle className="w-3 h-3" />}
            </button>
            <div className="min-w-0 flex-1">
              <div className={`font-display font-semibold ${t.done ? "line-through text-slate-500" : "text-slate-900"}`}>
                {t.title}
              </div>
              {t.detail && (
                <p className="mt-1 text-sm text-slate-600 leading-relaxed whitespace-pre-line">{t.detail}</p>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-slate-400">
                <span>Added {fmt(t.created_at)}</span>
                {t.done && t.completed_by && (
                  <span className="inline-flex items-center gap-1">
                    <User className="w-3 h-3" /> Completed by {t.completed_by} · {fmt(t.completed_at)}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => startEdit(t)}
                data-testid={`todo-edit-${t.id}`}
                className="text-xs font-semibold text-slate-700 hover:text-slate-900"
              >
                Edit
              </button>
              <button
                onClick={() => remove(t.id)}
                data-testid={`todo-delete-${t.id}`}
                className="inline-flex items-center text-xs text-red-600 hover:text-red-800"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </li>
        ))}
        {!loading && visible.length === 0 && (
          <li className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <ListTodo className="w-6 h-6 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No tasks {showDone ? "yet" : "open"}. Add one above.</p>
          </li>
        )}
      </ul>

      <p className="mt-6 text-[11px] text-slate-400">
        Signed in as <span className="font-medium text-slate-600">{user?.email}</span>. Completions are stamped with your email.
      </p>
    </ToolPageShell>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400">{label}</div>
      <div className="font-display text-2xl font-bold text-slate-900 tabular mt-1">{value}</div>
    </div>
  );
}
